import React from 'react';
import {
  Drawer, Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, IconButton, Tooltip, Divider, useTheme, useMediaQuery,
} from '@mui/material';
import {
  Home, Receipt, People, Inventory, Assessment, Settings,
  ChevronLeft, ChevronRight, Engineering,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import './AppSidebar.css';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;

interface AppSidebarProps {
  open: boolean;
  onToggle: () => void;
  darkMode: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const NAV_ITEMS = [
  { label: 'Home', icon: <Home />, route: '/home' },
  { label: 'Invoices', icon: <Receipt />, route: '/invoices' },
  { label: 'Customers', icon: <People />, route: '/customers' },
  { label: 'Products', icon: <Inventory />, route: '/products' },
  { label: 'Reports', icon: <Assessment />, route: '/reports' },
  { label: 'Settings', icon: <Settings />, route: '/settings' },
];

const AppSidebar: React.FC<AppSidebarProps> = ({ open, onToggle, darkMode, mobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleNav = (route: string) => {
    navigate(route);
    if (isMobile) onMobileClose();
  };

  const showLabels = (isMobile && mobileOpen) || (!isMobile && open);

  const sidebarContent = (
    <Box className={`sidebar-inner ${darkMode ? 'dark' : 'light'}`}>
      {/* Brand */}
      <Box className="sidebar-brand">
        <Box className="sidebar-brand-icon">
          <Engineering fontSize="small" />
        </Box>
        {open && (
          <Typography variant="subtitle1" className="sidebar-brand-text">
            Regal Engineers
          </Typography>
        )}
      </Box>

      <Divider className="sidebar-divider" />

      {/* Nav items */}
      <List className="sidebar-nav" disablePadding>
        {NAV_ITEMS.map((item) => {
          const active = location.pathname === item.route;
          const labelTooltip = showLabels ? '' : item.label;
          const collapsedClass = showLabels ? '' : 'collapsed';
          return (
            <ListItem key={item.route} disablePadding>
              <Tooltip title={labelTooltip} placement="right" arrow>
                <ListItemButton
                  onClick={() => handleNav(item.route)}
                  className={`sidebar-nav-item ${active ? 'active' : ''} ${collapsedClass}`}
                >
                  <ListItemIcon className={`sidebar-nav-icon ${active ? 'active' : ''}`}>
                    {item.icon}
                  </ListItemIcon>
                  {showLabels && (
                    <ListItemText
                      primary={item.label}
                      className="sidebar-nav-label"
                      primaryTypographyProps={{ variant: 'body2', fontWeight: active ? 600 : 400 }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      {/* Collapse toggle (desktop only) */}
      {!isMobile && (
        <Box className="sidebar-toggle">
          <Divider className="sidebar-divider" />
          <IconButton onClick={onToggle} size="small" className="sidebar-toggle-btn">
            {open ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: open
            ? theme.transitions.duration.enteringScreen
            : theme.transitions.duration.leavingScreen,
        }),
        '& .MuiDrawer-paper': {
          width: open ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
          overflowX: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: open
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
          border: 'none',
          borderRight: '1px solid',
          borderColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );
};

export default AppSidebar;
